import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ApplicationRecord, RecordStatus, StageId, TimelineEvent } from '../types/domain';
import type { GeneratedReport, RecommendationRule } from '../types/ai';
import { applications as seededApplications } from '../data/applications';
import { criteriaSets } from '../data/criteria';
import { recommendationRules as seededRules } from '../data/criteria';
import { reports as seededReports } from '../data/reports';
import { currentOfficer } from '../data/officers';
import { nextStage } from '../utils/stages';
import type { Bands } from '../utils/scoring';
import { DEFAULT_BANDS } from '../utils/scoring';
export type WeightMap = Record<string, Record<string, number>>;
interface DecisionInput {
  rationale: string;
  officer?: string;
}
interface PlatformValue {
  applications: ApplicationRecord[];
  reports: GeneratedReport[];
  rules: RecommendationRule[];
  weights: WeightMap;
  bands: Record<string, Bands>;
  applicationById: (id: string) => ApplicationRecord | undefined;
  advanceStage: (id: string, input: DecisionInput & {
    override: boolean;
  }) => void;
  recordDecision: (id: string, decision: 'approve' | 'reject' | 'request-info' | 'escalate', input: DecisionInput) => void;
  assignOfficer: (id: string, officer: string) => void;
  setRecommendationStatus: (appId: string, recId: string, status: 'accepted' | 'overridden') => void;
  setWeight: (setId: string, criterionId: string, value: number) => void;
  setBands: (setId: string, bands: Bands) => void;
  toggleRule: (ruleId: string) => void;
  addReport: (report: GeneratedReport) => void;
}
const PlatformContext = createContext<PlatformValue | null>(null);
function initialWeights(): WeightMap {
  const map: WeightMap = {};
  criteriaSets.forEach(set => {
    map[set.id] = {};
    set.criteria.forEach(criterion => {
      map[set.id][criterion.id] = criterion.weight;
    });
  });
  return map;
}
function initialBands(): Record<string, Bands> {
  const map: Record<string, Bands> = {};
  criteriaSets.forEach(set => {
    map[set.id] = {
      autoAdvance: set.bands.autoAdvance,
      manualReview: set.bands.manualReview
    };
  });
  return map;
}
function makeEvent(label: string, detail?: string, actor = currentOfficer.name): TimelineEvent {
  return {
    id: `tl-${Math.random().toString(36).slice(2, 9)}`,
    on: new Date().toISOString().slice(0, 10),
    actor,
    label,
    detail
  };
}
const decisionStatus: Record<string, RecordStatus> = {
  approve: 'active',
  reject: 'rejected',
  'request-info': 'info-requested',
  escalate: 'in-review'
};
const decisionLabel: Record<string, string> = {
  approve: 'Approved at stage',
  reject: 'Application declined',
  'request-info': 'Information requested',
  escalate: 'Escalated to committee'
};
export function PlatformProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [applications, setApplications] = useState<ApplicationRecord[]>(seededApplications);
  const [reports, setReports] = useState<GeneratedReport[]>(seededReports);
  const [rules, setRules] = useState<RecommendationRule[]>(seededRules);
  const [weights, setWeights] = useState<WeightMap>(initialWeights);
  const [bands, setBandsState] = useState<Record<string, Bands>>(initialBands);
  const applicationById = useCallback((id: string) => applications.find(a => a.id === id), [applications]);
  const updateApplication = useCallback((id: string, updater: (record: ApplicationRecord) => ApplicationRecord) => {
    setApplications(prev => prev.map(record => record.id === id ? updater(record) : record));
  }, []);
  const advanceStage = useCallback((id: string, input: DecisionInput & {
    override: boolean;
  }) => {
    updateApplication(id, record => {
      const target: StageId | null = nextStage(record.track, record.stage);
      if (!target) return record;
      const label = input.override ? `Gate overridden — advanced to ${target}` : `Advanced to ${target}`;
      return {
        ...record,
        stage: target,
        status: 'active',
        stageEnteredOn: new Date().toISOString().slice(0, 10),
        daysInStage: 0,
        timeline: [...record.timeline, makeEvent(label, input.rationale, input.officer)]
      };
    });
  }, [updateApplication]);
  const recordDecision = useCallback<PlatformValue['recordDecision']>((id, decision, input) => {
    updateApplication(id, record => ({
      ...record,
      status: decisionStatus[decision] ?? record.status,
      timeline: [...record.timeline, makeEvent(decisionLabel[decision], input.rationale, input.officer)]
    }));
  }, [updateApplication]);
  const assignOfficer = useCallback((id: string, officer: string) => {
    updateApplication(id, record => ({
      ...record,
      officer,
      timeline: [...record.timeline, makeEvent('Officer reassigned', `Assigned to ${officer}`)]
    }));
  }, [updateApplication]);
  const setRecommendationStatus = useCallback<PlatformValue['setRecommendationStatus']>((appId, recId, status) => {
    updateApplication(appId, record => {
      const recommendation = record.recommendations.find(r => r.id === recId);
      return {
        ...record,
        recommendations: record.recommendations.map(r => r.id === recId ? {
          ...r,
          status
        } : r),
        timeline: [...record.timeline, makeEvent(status === 'accepted' ? 'Recommendation accepted' : 'Recommendation overridden', recommendation?.title)]
      };
    });
  }, [updateApplication]);
  const setWeight = useCallback((setId: string, criterionId: string, value: number) => {
    setWeights(prev => ({
      ...prev,
      [setId]: {
        ...prev[setId],
        [criterionId]: value
      }
    }));
  }, []);
  const setBands = useCallback((setId: string, next: Bands) => {
    setBandsState(prev => ({
      ...prev,
      [setId]: next
    }));
  }, []);
  const toggleRule = useCallback((ruleId: string) => {
    setRules(prev => prev.map(rule => rule.id === ruleId ? {
      ...rule,
      active: !rule.active
    } : rule));
  }, []);
  const addReport = useCallback((report: GeneratedReport) => {
    setReports(prev => [report, ...prev]);
  }, []);
  const value = useMemo<PlatformValue>(() => ({
    applications,
    reports,
    rules,
    weights,
    bands,
    applicationById,
    advanceStage,
    recordDecision,
    assignOfficer,
    setRecommendationStatus,
    setWeight,
    setBands,
    toggleRule,
    addReport
  }), [applications, reports, rules, weights, bands, applicationById, advanceStage, recordDecision, assignOfficer, setRecommendationStatus, setWeight, setBands, toggleRule, addReport]);
  return <PlatformContext.Provider value={value} data-id="element-838" data-mp="2bysb2m4ig5uw">{children}</PlatformContext.Provider>;
}
export function usePlatform(): PlatformValue {
  const context = useContext(PlatformContext);
  if (!context) throw new Error('usePlatform must be used inside PlatformProvider');
  return context;
}
export const DEFAULT_SCORE_BANDS = DEFAULT_BANDS;